import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #getAnalyticsContext() {
    if (typeof window.getWilsonAnalyticsUser === 'function') {
      const shared = window.getWilsonAnalyticsUser();
      if (shared && typeof shared.authentication_status === 'string' && shared.authentication_status.length > 0) {
        return {
          authenticationStatus: shared.authentication_status,
          shopifyCustomerId: typeof shared.shopify_customer_id === 'string' ? shared.shopify_customer_id : null,
          userCartId: typeof shared.user_cart_id === 'string' ? shared.user_cart_id : null,
          userGroup: typeof shared.user_group === 'string' ? shared.user_group : null,
          userType: typeof shared.user_type === 'string' ? shared.user_type : null,
          userHeId: typeof shared.user_HeID === 'string' ? shared.user_HeID : null,
        };
      }
    }

    const authenticationStatus = this.dataset.authenticationStatus;
    const shopifyCustomerId = this.dataset.shopifyCustomerId || null;
    const userCartId = this.dataset.userCartId || null;
    const userGroup = this.dataset.userGroup || null;
    const userType = this.dataset.userType || null;
    const userHeId = this.dataset.userHeId || null;
    if (typeof authenticationStatus === 'string' && authenticationStatus.length > 0) {
      return { authenticationStatus, shopifyCustomerId, userCartId, userGroup, userType, userHeId };
    }

    const dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    for (let index = dataLayer.length - 1; index >= 0; index -= 1) {
      const entry = dataLayer[index];
      if (!entry || typeof entry !== 'object') continue;

      const entryAuthenticationStatus =
        typeof entry.authentication_status === 'string' ? entry.authentication_status : null;
      const entryShopifyCustomerId =
        typeof entry.shopify_customer_id === 'string' ? entry.shopify_customer_id : null;
      const entryUserCartId = typeof entry.user_cart_id === 'string' ? entry.user_cart_id : null;
      const entryUserGroup = typeof entry.user_group === 'string' ? entry.user_group : null;
      const entryUserType = typeof entry.user_type === 'string' ? entry.user_type : null;
      const entryUserHeId = typeof entry.user_HeID === 'string' ? entry.user_HeID : null;

      if (!entryAuthenticationStatus) continue;
      return {
        authenticationStatus: entryAuthenticationStatus,
        shopifyCustomerId: entryShopifyCustomerId,
        userCartId: entryUserCartId,
        userGroup: entryUserGroup,
        userType: entryUserType,
        userHeId: entryUserHeId,
      };
    }

    return {
      authenticationStatus: null,
      shopifyCustomerId: null,
      userCartId: null,
      userGroup: null,
      userType: null,
      userHeId: null,
    };
  }

  #trackAddCoupon(coupon, validationResult) {
    if (typeof coupon !== 'string' || coupon.length === 0) return;

    const payload = {
      event: 'add_coupon',
      coupon,
      validation_result: validationResult,
    };

    if (typeof window.pushWilsonDataLayer === 'function') {
      window.pushWilsonDataLayer(payload);
      return;
    }

    const { authenticationStatus, shopifyCustomerId, userCartId, userGroup, userType, userHeId } =
      this.#getAnalyticsContext();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...payload,
      authentication_status: authenticationStatus,
      shopify_customer_id: shopifyCustomerId,
      user_cart_id: userCartId,
      user_group: userGroup,
      user_type: userType,
      user_HeID: userHeId,
    });
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        this.#trackAddCoupon(discountCodeValue, 'Success');
        return;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        this.#trackAddCoupon(discountCodeValue, 'Failed');
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#trackAddCoupon(discountCodeValue, 'Failed');
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      this.#trackAddCoupon(discountCodeValue, 'Success');
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      this.#trackAddCoupon(discountCodeValue, 'Failed');
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
